import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { KeyboardShortcutConfig, ModifierKey, ShortcutPriority, ShortcutRegistryEntry } from '../models/keyboard-shortcut.models';

/**
 * Service for managing keyboard shortcuts across the application.
 * Supports multiple contexts, priorities, and modifier key combinations.
 * 
 * @example
 * ```typescript
 * constructor(private shortcutService: KeyboardShortcutService) {
 *   this.shortcutService.register({
 *     id: 'save-document',
 *     keys: 's',
 *     modifiers: [ModifierKey.CTRL],
 *     handler: () => this.saveDocument(),
 *     description: 'Save document',
 *     context: 'editor',
 *     preventDefault: true
 *   });
 * }
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class KeyboardShortcutService implements OnDestroy {
    private shortcuts: Map<string, ShortcutRegistryEntry> = new Map();
    private shortcutsByContext: Map<string, Set<string>> = new Map();
    private shortcutsByKeyString: Map<string, ShortcutRegistryEntry[]> = new Map();
    private registrationCounter = 0;

    constructor(private zone: NgZone) {
        this.initializeGlobalListener();
    }

    /**
     * Detect if the current platform is Mac
     * @returns True if running on Mac, false otherwise
     */
    isMacPlatform(): boolean {
        return navigator.platform.toUpperCase().indexOf('MAC') >= 0 ||
            navigator.userAgent.toUpperCase().indexOf('MAC') >= 0;
    }

    /**
     * Format a keyboard shortcut for display based on platform
     * @param key - The main key (e.g., 's', 'k', 'Enter')
     * @param modifier - The modifier key (e.g., ModifierKey.CMD, ModifierKey.CTRL)
     * @returns Formatted shortcut string (e.g., '⌘S' on Mac, 'Ctrl+S' on Windows/Linux)
     */
    formatShortcutForDisplay(key: string, modifier?: ModifierKey): string {
        const isMac = this.isMacPlatform();
        const upperKey = key.toUpperCase();

        if (!modifier) {
            return upperKey;
        }

        if (modifier === ModifierKey.CMD || modifier === ModifierKey.CTRL) {
            // Show Cmd on Mac, Ctrl on Windows/Linux
            if (isMac && modifier === ModifierKey.CMD) {
                return `⌘${upperKey}`;
            } else if (modifier === ModifierKey.CTRL) {
                return `Ctrl+${upperKey}`;
            }
        }

        // Handle other modifiers
        switch (modifier) {
            case ModifierKey.SHIFT:
                return `Shift+${upperKey}`;
            case ModifierKey.ALT:
                return `Alt+${upperKey}`;
            default:
                return `${upperKey}`;
        }
    }

    /**
     * Format keyboard shortcut with multiple modifiers
     * @param key - The main key
     * @param modifiers - Array of modifier keys
     * @returns Formatted shortcut string
     */
    formatShortcutWithModifiers(key: string, modifiers: ModifierKey[]): string {
        const isMac = this.isMacPlatform();
        const upperKey = key.toUpperCase();
        const modifierStrings: string[] = [];

        modifiers.forEach(modifier => {
            switch (modifier) {
                case ModifierKey.CMD:
                    if (isMac) {
                        modifierStrings.push('⌘');
                    } else {
                        modifierStrings.push('Ctrl');
                    }
                    break;
                case ModifierKey.CTRL:
                    modifierStrings.push('Ctrl');
                    break;
                case ModifierKey.SHIFT:
                    modifierStrings.push('Shift');
                    break;
                case ModifierKey.ALT:
                    modifierStrings.push('Alt');
                    break;
            }
        });

        return modifierStrings.length > 0
            ? `${modifierStrings.join('+')}+${upperKey}`
            : upperKey;
    }

    /**
     * Register a new keyboard shortcut
     * @param config - Configuration object for the shortcut
     * @returns Registration ID for unregistering the shortcut
     */
    register(config: KeyboardShortcutConfig): string {
        const baseRegistrationId = `shortcut_${++this.registrationCounter}_${Date.now()}`;
        const keys = Array.isArray(config.keys) ? config.keys : [config.keys];

        // Store all registration IDs created for this config
        const createdRegistrationIds: string[] = [];

        keys.forEach((key, index) => {
            const keyString = this.normalizeKeyString(key, config.modifiers || []);
            // Create unique registration ID for each key combination
            const registrationId = keys.length > 1
                ? `${baseRegistrationId}_key_${index}`
                : baseRegistrationId;

            const entry: ShortcutRegistryEntry = {
                ...config,
                keys: config.keys,
                keyString,
                registrationId,
                registeredAt: Date.now(),
                enabled: config.enabled !== false,
                priority: config.priority || ShortcutPriority.NORMAL,
                preventDefault: config.preventDefault !== false,
                stopPropagation: config.stopPropagation || false,
                context: config.context || 'global'
            };

            // Store by registration ID
            this.shortcuts.set(registrationId, entry);
            createdRegistrationIds.push(registrationId);

            // Store by key string for quick lookup
            if (!this.shortcutsByKeyString.has(keyString)) {
                this.shortcutsByKeyString.set(keyString, []);
            }
            this.shortcutsByKeyString.get(keyString)!.push(entry);

            // Store by context
            const context = entry.context || 'global';
            if (!this.shortcutsByContext.has(context)) {
                this.shortcutsByContext.set(context, new Set());
            }
            this.shortcutsByContext.get(context)!.add(registrationId);
        });

        // Store mapping of base ID to all created IDs for bulk unregistration
        if (keys.length > 1) {
            this.shortcuts.set(baseRegistrationId, {
                ...config,
                keys: config.keys,
                keyString: '', // Not used for base entry
                registrationId: baseRegistrationId,
                registeredAt: Date.now(),
                enabled: false, // Base entry is disabled, only used for tracking
                priority: ShortcutPriority.LOW,
                preventDefault: false,
                stopPropagation: false,
                context: config.context || 'global'
            } as ShortcutRegistryEntry);
        }

        return baseRegistrationId;
    }

    /**
     * Unregister a shortcut by registration ID
     * @param registrationId - The registration ID returned from register()
     */
    unregister(registrationId: string): void {
        const entry = this.shortcuts.get(registrationId);
        if (!entry) {
            return;
        }

        // If this is a base registration ID for multiple keys, unregister all related keys
        const keys = Array.isArray(entry.keys) ? entry.keys : [entry.keys];
        if (keys.length > 1) {
            // Find and unregister all related entries
            const keysArray = Array.isArray(entry.keys) ? entry.keys : [entry.keys];
            keysArray.forEach((key, index) => {
                const keyString = this.normalizeKeyString(key, entry.modifiers || []);
                const relatedRegistrationId = `${registrationId}_key_${index}`;
                this.unregisterEntry(relatedRegistrationId);
            });
            // Remove base entry
            this.shortcuts.delete(registrationId);
            return;
        }

        // Single key shortcut
        this.unregisterEntry(registrationId);
    }

    /**
     * Internal method to unregister a single entry
     * @param registrationId - The registration ID to unregister
     */
    private unregisterEntry(registrationId: string): void {
        const entry = this.shortcuts.get(registrationId);
        if (!entry || !entry.keyString) {
            return;
        }

        // Remove from key string map
        const keyStringEntries = this.shortcutsByKeyString.get(entry.keyString);
        if (keyStringEntries) {
            const index = keyStringEntries.findIndex(e => e.registrationId === registrationId);
            if (index !== -1) {
                keyStringEntries.splice(index, 1);
                if (keyStringEntries.length === 0) {
                    this.shortcutsByKeyString.delete(entry.keyString);
                }
            }
        }

        // Remove from context map
        const contextEntries = this.shortcutsByContext.get(entry.context || 'global');
        if (contextEntries) {
            contextEntries.delete(registrationId);
            if (contextEntries.size === 0) {
                this.shortcutsByContext.delete(entry.context || 'global');
            }
        }

        // Remove from main map
        this.shortcuts.delete(registrationId);
    }

    /**
     * Unregister all shortcuts for a specific context
     * @param context - The context identifier
     */
    unregisterByContext(context: string): void {
        const contextEntries = this.shortcutsByContext.get(context);
        if (!contextEntries) {
            return;
        }

        const registrationIds = Array.from(contextEntries);
        registrationIds.forEach(id => this.unregister(id));
    }

    /**
     * Unregister shortcuts by ID pattern
     * @param idPattern - Pattern to match shortcut IDs
     */
    unregisterByIdPattern(idPattern: string | RegExp): void {
        const pattern = typeof idPattern === 'string' ? new RegExp(idPattern) : idPattern;
        const toRemove: string[] = [];

        this.shortcuts.forEach((entry, registrationId) => {
            if (pattern.test(entry.id)) {
                toRemove.push(registrationId);
            }
        });

        toRemove.forEach(id => this.unregister(id));
    }

    /**
     * Enable or disable a shortcut
     * @param registrationId - The registration ID
     * @param enabled - Whether to enable or disable
     */
    setEnabled(registrationId: string, enabled: boolean): void {
        const entry = this.shortcuts.get(registrationId);
        if (entry) {
            entry.enabled = enabled;
        }
    }

    /**
     * Enable or disable all shortcuts in a context
     * @param context - The context identifier
     * @param enabled - Whether to enable or disable
     */
    setContextEnabled(context: string, enabled: boolean): void {
        const contextEntries = this.shortcutsByContext.get(context);
        if (!contextEntries) {
            return;
        }

        contextEntries.forEach(registrationId => {
            this.setEnabled(registrationId, enabled);
        });
    }

    /**
     * Get all registered shortcuts
     * @returns Array of shortcut configurations
     */
    getAllShortcuts(): ShortcutRegistryEntry[] {
        return Array.from(this.shortcuts.values())
            .filter(entry => entry.keyString || entry.keys); // Filter out base tracking entries
    }

    /**
     * Get shortcuts for a specific context
     * @param context - The context identifier
     * @returns Array of shortcut configurations
     */
    getShortcutsByContext(context: string): ShortcutRegistryEntry[] {
        const contextEntries = this.shortcutsByContext.get(context);
        if (!contextEntries) {
            return [];
        }

        return Array.from(contextEntries)
            .map(id => this.shortcuts.get(id))
            .filter((entry): entry is ShortcutRegistryEntry => entry !== undefined);
    }

    /**
     * Check if a shortcut is registered
     * @param id - The shortcut ID
     * @returns True if shortcut exists
     */
    hasShortcut(id: string): boolean {
        return Array.from(this.shortcuts.values()).some(entry => entry.id === id);
    }

    /**
     * Normalize key string for consistent matching
     * @param key - The key to normalize
     * @param modifiers - Array of modifier keys
     * @returns Normalized key string
     */
    private normalizeKeyString(key: string, modifiers: ModifierKey[]): string {
        const normalizedKey = key?.toLowerCase();
        const normalizedModifiers = modifiers
            .map(m => m?.toLowerCase())
            .sort()
            .join('+');

        return normalizedModifiers ? `${normalizedModifiers}+${normalizedKey}` : normalizedKey;
    }

    /**
     * Initialize global keyboard event listener
     */
    private initializeGlobalListener(): void {
        this.zone.runOutsideAngular(() => {
            document.addEventListener('keydown', (event: KeyboardEvent) => {
                this.handleKeyDown(event);
            });
        });
    }

    /**
     * Handle keydown events
     * @param event - Keyboard event
     */
    private handleKeyDown(event: KeyboardEvent): void {
        const modifiers: ModifierKey[] = [];

        if (event.ctrlKey || event.metaKey) {
            modifiers.push(event.metaKey ? ModifierKey.CMD : ModifierKey.CTRL);
        }
        if (event.shiftKey) {
            modifiers.push(ModifierKey.SHIFT);
        }
        if (event.altKey) {
            modifiers.push(ModifierKey.ALT);
        }

        // Handle special keys
        let key = event.key;
        if (key === 'Meta') {
            key = event.code.replace('Meta', '')?.toLowerCase();
        }

        const keyString = this.normalizeKeyString(key, modifiers);
        const entries = this.shortcutsByKeyString.get(keyString);

        if (!entries || entries.length === 0) {
            return;
        }

        // Filter enabled shortcuts
        const enabledEntries = entries.filter(entry => entry.enabled !== false);

        if (enabledEntries.length === 0) {
            return;
        }

        // Sort by priority (highest first)
        enabledEntries.sort((a, b) => {
            const priorityA = a.priority || ShortcutPriority.NORMAL;
            const priorityB = b.priority || ShortcutPriority.NORMAL;
            return priorityB - priorityA;
        });

        // Find the highest-priority entry whose target matches the event source.
        // Previous code only checked enabledEntries[0] and returned early if its target
        // didn't match — this prevented other registered shortcuts (e.g. from a different
        // component with a different target) from ever running.
        const entry = enabledEntries.find(e =>
            !e.target || e.target.contains(event.target as Node)
        );

        if (!entry) {
            return;
        }

        this.zone.run(() => {
            if (entry.preventDefault) {
                event.preventDefault();
            }

            if (entry.stopPropagation) {
                event.stopPropagation();
            }

            const result = entry.handler(event);

            // If handler returns false, try the next matching entry
            if (result === false) {
                const nextEntry = enabledEntries
                    .slice(enabledEntries.indexOf(entry) + 1)
                    .find(e => !e.target || e.target.contains(event.target as Node));
                if (nextEntry) {
                    if (nextEntry.preventDefault) event.preventDefault();
                    if (nextEntry.stopPropagation) event.stopPropagation();
                    nextEntry.handler(event);
                }
            }
        });
    }

    /**
     * Cleanup on service destruction
     */
    ngOnDestroy(): void {
        this.shortcuts.clear();
        this.shortcutsByContext.clear();
        this.shortcutsByKeyString.clear();
    }
}
